import React from 'react'
import { API_BASE } from '../config'

// Revive booking assistant — website widget (full-screen on mobile, panel on desktop).
// phone → OTP → safety triage → reason → concern → mode → slot → details →
// review → confirm → (optional) upload photos before the visit.
// Also: "Request a call" (outbound) and a RAG Q&A chat.
// Reuses /api/otp/*, /api/booking/*, /api/appointments/:id/photos.

// Clinic number — used on the urgent-care screen.
const CLINIC_PHONE = '+91 79815 89588'

// ⚠️ REVIEW/REPLACE with clinic-approved, DPDP-compliant consent wording.
const CONSENT_TEXT =
  'I consent to Revive storing my personal and medical information (including any photos I share) for the purpose of my consultation and treatment.'

// Reason routes (single-doctor clinic → specialist auto-assigned; concern is captured for the doctor).
const ROUTES = {
  skin:      { label: 'New skin consultation',      icon: '🩺', service: 'Skin consultation',
    concerns: ['Acne or pimples', 'Rash or itching', 'Pigmentation or dark spots', 'Infection', 'Mole or skin growth', 'Nail problem', 'Not sure'] },
  followup:  { label: 'Existing-patient follow-up', icon: '🔁', service: 'Follow-up',
    concerns: ['Treatment follow-up', 'Medication concern', 'Symptoms have changed', 'Book the next session', 'Talk to the clinic'] },
  hair:      { label: 'Hair or scalp consultation', icon: '💇', service: 'Hair & scalp consultation',
    concerns: ['Hair fall', 'Dandruff or itchy scalp', 'Bald patches', 'Hair thinning', 'Scalp infection', 'Not sure'] },
  cosmetic:  { label: 'Cosmetic skin treatment',    icon: '✨', service: 'Cosmetic assessment',
    concerns: ['Improve acne scars', 'Reduce pigmentation', 'Reduce wrinkles or fine lines', 'Skin rejuvenation', 'Laser hair reduction', 'Chemical peel', 'I need help choosing'] },
  procedure: { label: 'Book a procedure or test',   icon: '🧾', service: 'Procedure / test' },
}

// Urgent-care red flags — any of these halts normal booking.
const DANGER_SIGNS = [
  'Severe swelling of face, lips or throat',
  'Difficulty breathing',
  'A rapidly worsening rash or blistering',
  'A serious burn',
  'Another medical emergency',
]

const C = {
  navy: '#171a2b', navy2: '#232744', indigo: '#4f46e5', indigoD: '#4338ca',
  bg: '#f4f5f9', card: '#ffffff', line: '#e6e8f0', ink: '#1e2130', mut: '#6b7280',
  soft: '#eef0f9', err: '#c0392b', ok: '#22c55e',
  warn: '#7a5b00', warnBg: '#fff7e6', warnLine: '#ffe0a3', dangerBg: '#fdecea',
}

const fmtDate = (ds) => {
  try {
    const d = new Date(ds + 'T00:00:00Z')
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
  } catch { return ds }
}

export default function ChatbotWidget({ bottomInset = 0 }) {
  const [open, setOpen] = React.useState(false)
  const [isMobile, setIsMobile] = React.useState(typeof window !== 'undefined' && window.innerWidth < 640)
  React.useEffect(() => {
    const onR = () => setIsMobile(window.innerWidth < 640)
    window.addEventListener('resize', onR)
    return () => window.removeEventListener('resize', onR)
  }, [])

  const [step, setStep] = React.useState('phone')
  // phone | otp | triage | emergency | reason | concern | procedure |
  // mode | book | details | review | done | called | chat
  const [phone, setPhone] = React.useState('')
  const [otp, setOtp] = React.useState('')
  const [token, setToken] = React.useState('')
  const [user, setUser] = React.useState({ name: '', email: '' })
  const [busy, setBusy] = React.useState(false)
  const [err, setErr] = React.useState('')
  const [note, setNote] = React.useState('')

  // routing state
  const [route, setRoute] = React.useState('')
  const [concern, setConcern] = React.useState('')
  const [mode, setMode] = React.useState('clinic')
  const [treatment, setTreatment] = React.useState(null)
  const [rx, setRx] = React.useState({ eligible: false, items: [] })
  const [danger, setDanger] = React.useState([])

  // booking state
  const [doctor, setDoctor] = React.useState(null)
  const [avail, setAvail] = React.useState([])
  const [selDate, setSelDate] = React.useState('')
  const [selSlot, setSelSlot] = React.useState('')
  const [booked, setBooked] = React.useState(null)

  // patient details
  const [fullName, setFullName] = React.useState('')
  const [dob, setDob] = React.useState('')
  const [gender, setGender] = React.useState('')
  const [consent, setConsent] = React.useState(false)

  // post-confirm photo upload
  const [uploading, setUploading] = React.useState(false)
  const [uploadDone, setUploadDone] = React.useState(false)

  // AI Q&A chat
  const [chatMsgs, setChatMsgs] = React.useState([])
  const [chatInput, setChatInput] = React.useState('')
  const [chatBusy, setChatBusy] = React.useState(false)

  const clean = phone.replace(/\D/g, '').slice(-10)

  const call = async (path, body, method = 'POST') => {
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      ...(method === 'POST' ? { body: JSON.stringify(body) } : {}),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) { const e = new Error(data.message || 'Something went wrong'); e.data = data; e.status = res.status; throw e }
    return data
  }

  const sendOtp = async () => {
    setErr('')
    if (clean.length !== 10) { setErr('Enter a valid 10-digit mobile number'); return }
    setBusy(true)
    try {
      const d = await call('/api/otp/send', { phone: clean })
      setNote(d.dev && d.otp ? `Dev OTP: ${d.otp}` : 'OTP sent on WhatsApp')
      setStep('otp')
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const verifyOtp = async () => {
    setErr('')
    if (otp.replace(/\D/g, '').length < 4) { setErr('Enter the OTP'); return }
    setBusy(true)
    try {
      const d = await call('/api/otp/verify', { phone: clean, otp: otp.trim() })
      setToken(d.token); setUser({ name: d.user?.name || '', email: d.user?.email || clean })
      setFullName(d.user?.name || '')
      // Pre-fill a returning patient's details from their stored profile.
      try {
        const meRes = await fetch(`${API_BASE}/api/booking/me`, { headers: { Authorization: `Bearer ${d.token}` } })
        const me = await meRes.json().catch(() => ({}))
        if (me.found) { if (me.name) setFullName(me.name); if (me.dob) setDob(me.dob); if (me.gender) setGender(me.gender) }
      } catch { /* prefill is best-effort */ }
      setNote(''); setStep('triage')
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const continueFromTriage = () => { setErr(''); setStep('reason') }

  const chooseRoute = async (key) => {
    setErr(''); setRoute(key); setConcern('')
    if (key === 'followup') {
      setBusy(true)
      try { const t = await call('/api/booking/my-treatment', null, 'GET'); setTreatment(t.found ? t : null) }
      catch { setTreatment(null) } finally { setBusy(false) }
      setStep('concern')
    } else if (key === 'procedure') {
      setBusy(true)
      try { const v = await call('/api/booking/verify-prescription', null, 'GET'); setRx({ eligible: !!v.eligible, items: v.items || [] }) }
      catch { setRx({ eligible: false, items: [] }) } finally { setBusy(false) }
      setStep('procedure')
    } else {
      setStep('concern')
    }
  }

  const chooseConcern = (c) => {
    setErr(''); setConcern(c)
    if (c === 'Talk to the clinic') { requestCall(); return }
    setStep('mode')
  }

  const chooseMode = async (m) => {
    setErr(''); setMode(m); setBusy(true)
    try {
      const [docs, av] = await Promise.all([
        call('/api/booking/doctors', null, 'GET').catch(() => ({ doctors: [] })),
        call('/api/booking/availability?days=14', null, 'GET'),
      ])
      setDoctor((docs.doctors || [])[0] || null)
      setAvail(av.availability || [])
      setSelDate((av.availability || [])[0]?.date || '')
      setSelSlot('')
      setStep('book')
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const requestCall = async () => {
    setErr(''); setBusy(true)
    try { await call('/api/booking/request-call', { phone: clean, name: fullName || user.name }); setStep('called') }
    catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const goToDetails = () => {
    if (!selDate || !selSlot) { setErr('Please pick a date and time'); return }
    setErr(''); setStep('details')
  }

  const goToReview = () => {
    if (!fullName.trim()) { setErr('Please enter your full name'); return }
    if (!consent) { setErr('Please provide consent to continue'); return }
    setErr(''); setStep('review')
  }

  const confirmBooking = async () => {
    setErr(''); setBusy(true)
    try {
      await call('/api/booking/hold', { date: selDate, timeSlot: selSlot, email: user.email })
      const r = await call('/api/booking/create', {
        name: fullName || user.name || 'Patient', email: user.email, phone: clean,
        date: selDate, timeSlot: selSlot,
        service: ROUTES[route]?.service || 'Consultation',
        route, concern, mode, dob, gender, consent,
      })
      setBooked(r); setStep('done')
    } catch (e) {
      if (e.status === 409 && e.data?.slotTaken) { setErr('That slot was just taken — please pick another.'); await chooseMode(mode) }
      else if (e.status === 409 && e.data?.duplicate) { setErr('You already have an appointment on this date.') }
      else setErr(e.message)
    } finally { setBusy(false) }
  }

  const uploadPhotos = async (files) => {
    if (!booked?.appointmentId || !files || !files.length) return
    setErr(''); setUploading(true)
    try {
      const fd = new FormData()
      Array.from(files).slice(0, 10).forEach((f, i) => fd.append('photo' + i, f))
      const res = await fetch(`${API_BASE}/api/appointments/${booked.appointmentId}/photos`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      })
      if (!res.ok) throw new Error('Upload failed')
      setUploadDone(true)
    } catch { setErr('Could not upload — you can add photos from the Revive app anytime before your visit.') }
    finally { setUploading(false) }
  }

  const openChat = () => {
    setErr('')
    if (!chatMsgs.length) setChatMsgs([{ role: 'assistant', text: 'Hi! Ask me anything about Revive — treatments, pricing, hours, or how it works. Or tap "Book an appointment" below.' }])
    setStep('chat')
  }

  const sendChat = async () => {
    const text = chatInput.trim()
    if (!text || chatBusy) return
    const next = [...chatMsgs, { role: 'user', text }]
    setChatMsgs(next); setChatInput(''); setChatBusy(true)
    try {
      const res = await fetch(`${API_BASE}/api/assistant/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, history: chatMsgs.slice(-8) }),
      })
      const data = await res.json().catch(() => ({}))
      setChatMsgs([...next, { role: 'assistant', text: data.reply || "Sorry, I'm having trouble right now — please try again." }])
    } catch {
      setChatMsgs([...next, { role: 'assistant', text: "Sorry, I couldn't connect. Please try again." }])
    } finally { setChatBusy(false) }
  }

  const reset = () => {
    setStep('phone'); setPhone(''); setOtp(''); setToken(''); setErr(''); setNote('')
    setRoute(''); setConcern(''); setMode('clinic'); setTreatment(null); setRx({ eligible: false, items: [] }); setDanger([])
    setDoctor(null); setAvail([]); setSelDate(''); setSelSlot(''); setBooked(null)
    setFullName(''); setDob(''); setGender(''); setConsent(false); setUploadDone(false)
  }

  // ── styles ──────────────────────────────────────────────────────────────────
  const primaryBtn = (disabled) => ({
    width: '100%', padding: '.8rem', borderRadius: 12, border: 'none',
    background: disabled ? '#a9a7e0' : C.indigo, color: '#fff',
    fontSize: '.95rem', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit', marginTop: '.7rem',
  })
  const ghostBtn = { width: '100%', padding: '.75rem', borderRadius: 12, border: `1px solid ${C.line}`, background: C.card, color: C.indigoD, fontSize: '.92rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginTop: '.6rem' }
  const rowBtn = { width: '100%', textAlign: 'left', padding: '.85rem .9rem', borderRadius: 14, border: `1px solid ${C.line}`, background: C.card, color: C.ink, fontSize: '.92rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 1px 2px rgba(16,24,40,.04)' }
  const chip = (active) => ({
    padding: '.5rem .8rem', borderRadius: 999, border: `1px solid ${active ? C.indigo : C.line}`,
    background: active ? C.soft : C.card, color: active ? C.indigoD : C.ink, fontSize: '.82rem', fontWeight: 600,
    cursor: 'pointer', marginRight: 7, marginBottom: 7, display: 'inline-block',
  })
  const bubble = (children, tone) => (
    <div style={{
      background: tone === 'warn' ? C.warnBg : tone === 'danger' ? C.dangerBg : C.card,
      border: `1px solid ${tone === 'warn' ? C.warnLine : tone === 'danger' ? '#f5b7b1' : C.line}`,
      color: tone === 'warn' ? C.warn : C.ink,
      borderRadius: '6px 16px 16px 16px', padding: '12px 14px', fontSize: '.92rem', lineHeight: 1.55,
      marginBottom: 14, maxWidth: '94%', boxShadow: '0 1px 2px rgba(16,24,40,.04)',
    }}>{children}</div>
  )

  const dayObj = avail.find(a => a.date === selDate)
  const routeCfg = ROUTES[route] || {}

  // bottom input bar wiring (only on typing steps)
  const typingStep = step === 'phone' || step === 'otp' || step === 'chat'
  const barValue = step === 'phone' ? phone : step === 'otp' ? otp : chatInput
  const barSet = step === 'phone' ? setPhone : step === 'otp' ? setOtp : setChatInput
  const barSubmit = step === 'phone' ? sendOtp : step === 'otp' ? verifyOtp : sendChat
  const barPlaceholder = step === 'phone' ? 'Enter mobile number' : step === 'otp' ? 'Enter the OTP' : 'Type your question…'
  const barMode = step === 'chat' ? 'text' : 'numeric'

  const phaseVerify = ['phone', 'otp', 'triage', 'emergency'].includes(step)

  const panelStyle = isMobile
    ? { position: 'fixed', inset: 0, zIndex: 4000, background: C.bg, display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', system-ui, sans-serif", color: C.ink }
    : { position: 'fixed', bottom: 20, right: 20, zIndex: 4000, width: 400, height: 'min(660px, 88vh)', background: C.bg, borderRadius: 20, overflow: 'hidden', boxShadow: '0 24px 64px rgba(15,18,40,.32)', display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', system-ui, sans-serif", color: C.ink }

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} aria-label="Book with Revive"
          style={{ position: 'fixed', bottom: 20 + bottomInset, right: 20, zIndex: 4000, width: 62, height: 62, borderRadius: '50%', background: `linear-gradient(135deg,${C.navy},${C.indigo})`, color: '#fff', border: 'none', cursor: 'pointer', boxShadow: '0 10px 28px rgba(23,26,43,.4)', fontSize: 26 }}>💬</button>
      )}

      {open && (
        <div style={panelStyle}>
          {/* Header */}
          <div style={{ background: C.navy, color: '#fff', padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#fff', color: C.indigoD, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 20, flexShrink: 0 }}>R</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: '1.02rem', lineHeight: 1.2 }}>Revive Assist</div>
              <div style={{ fontSize: '.74rem', opacity: .8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: C.ok, display: 'inline-block' }} />
                Care navigation · online
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close" style={{ background: 'rgba(255,255,255,.12)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: 9, cursor: 'pointer', fontSize: 18, lineHeight: 1, flexShrink: 0 }}>×</button>
          </div>

          {/* Phase chips */}
          <div style={{ display: 'flex', gap: 8, padding: '10px 16px', background: C.card, borderBottom: `1px solid ${C.line}`, flexShrink: 0 }}>
            <span style={{ fontSize: '.72rem', fontWeight: 700, padding: '5px 11px', borderRadius: 999, background: phaseVerify ? C.soft : '#f1f2f6', color: phaseVerify ? C.indigoD : C.mut }}>Patient verification</span>
            <span style={{ fontSize: '.72rem', fontWeight: 700, padding: '5px 11px', borderRadius: 999, background: !phaseVerify ? C.soft : '#f1f2f6', color: !phaseVerify ? C.indigoD : C.mut }}>Care request</span>
          </div>

          {/* Body */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            {step === 'phone' && (
              <>
                {bubble(<>Welcome to Revive Assist. Enter your mobile number below to begin securely — we'll send an OTP on WhatsApp.</>)}
                <button onClick={openChat} style={{ background: 'none', border: 'none', color: C.indigoD, fontSize: '.85rem', cursor: 'pointer', fontFamily: 'inherit', padding: '4px 2px', fontWeight: 600 }}>💬 Just have a question? Ask here</button>
              </>
            )}

            {step === 'otp' && (
              <>
                {bubble(<>Enter the OTP sent to <b>{clean}</b>.</>)}
                <button onClick={sendOtp} style={{ background: 'none', border: 'none', color: C.indigoD, fontSize: '.82rem', cursor: 'pointer', fontFamily: 'inherit', padding: '4px 2px', fontWeight: 600 }}>Resend OTP</button>
              </>
            )}

            {step === 'triage' && (
              <>
                {bubble(<>✅ Verified{fullName ? `, ${fullName}` : ''}! One quick safety check before we book.</>)}
                <p style={{ margin: '0 0 8px', fontSize: '.9rem', fontWeight: 700 }}>Are you experiencing any of these right now?</p>
                {DANGER_SIGNS.map(s => {
                  const on = danger.includes(s)
                  return (
                    <button key={s} onClick={() => setDanger(on ? danger.filter(x => x !== s) : [...danger, s])}
                      style={{ ...rowBtn, borderColor: on ? C.err : C.line, background: on ? C.dangerBg : C.card }}>
                      <span style={{ fontSize: 16 }}>{on ? '⚠️' : '⚪'}</span><span>{s}</span>
                    </button>
                  )
                })}
                {danger.length > 0
                  ? <button style={{ ...primaryBtn(false), background: C.err }} onClick={() => setStep('emergency')}>Get urgent care →</button>
                  : <button style={primaryBtn(false)} onClick={continueFromTriage}>None of these — continue</button>}
              </>
            )}

            {step === 'emergency' && (
              <div style={{ textAlign: 'center', padding: '10px 0' }}>
                <div style={{ fontSize: 40 }}>🚑</div>
                <p style={{ fontSize: '1.02rem', fontWeight: 800, margin: '8px 0 4px', color: C.err }}>This may need urgent care</p>
                <p style={{ fontSize: '.9rem', color: C.mut, margin: '0 0 14px' }}>Please don't wait to book online. Call the clinic now, or go to your nearest emergency room.</p>
                <a href={`tel:${CLINIC_PHONE.replace(/\s/g, '')}`} style={{ ...primaryBtn(false), display: 'block', textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box', background: C.err }}>📞 Call {CLINIC_PHONE}</a>
                <button onClick={() => { setDanger([]); setStep('triage') }} style={{ ...ghostBtn }}>← Back</button>
              </div>
            )}

            {step === 'reason' && (
              <>
                {bubble(<>How can we help you today?</>)}
                {Object.entries(ROUTES).map(([key, r]) => (
                  <button key={key} style={rowBtn} disabled={busy} onClick={() => chooseRoute(key)}>
                    <span style={{ fontSize: 20 }}>{r.icon}</span><span>{r.label}</span>
                  </button>
                ))}
                <button style={ghostBtn} disabled={busy} onClick={requestCall}>📞 Request a call instead</button>
              </>
            )}

            {step === 'concern' && (
              <>
                {route === 'followup' && bubble(
                  treatment
                    ? <>Continuing with <b>{treatment.doctor}</b>{treatment.diagnosis ? <> · {treatment.diagnosis}</> : ''}{treatment.treatment ? <><br />Treatment: {treatment.treatment}</> : ''}</>
                    : <>We couldn't find a past treatment on file — that's okay, pick a reason below and the doctor will review your history.</>
                )}
                {route === 'cosmetic' && bubble('Cosmetic treatments start with a quick assessment consult — the doctor recommends what\'s right and safe for you before any procedure.', 'warn')}
                <p style={{ margin: '0 0 10px', fontSize: '.9rem', fontWeight: 700 }}>{route === 'followup' ? 'What do you need?' : 'What best describes your concern?'}</p>
                <div>{(routeCfg.concerns || []).map(c => <span key={c} style={chip(c === concern)} onClick={() => chooseConcern(c)}>{c}</span>)}</div>
                <button onClick={() => setStep('reason')} style={{ background: 'none', border: 'none', color: C.mut, fontSize: '.82rem', cursor: 'pointer', marginTop: 12, fontFamily: 'inherit' }}>← Back</button>
              </>
            )}

            {step === 'procedure' && (
              <>
                {rx.eligible ? (
                  <>
                    {bubble(<>Which procedure or test? These are based on your prescriptions / past consultations.</>)}
                    <div>{rx.items.map((it, i) => <span key={i} style={chip(it.text === concern)} onClick={() => chooseConcern(it.text)}>{it.text}{it.date ? ` · ${fmtDate(String(it.date).slice(0, 10))}` : ''}</span>)}</div>
                  </>
                ) : (
                  <>
                    {bubble(<>We don't have a prescription or prior consultation on file for a procedure yet. Let's start with a consultation — the doctor will advise the right procedure or test.</>, 'warn')}
                    <button style={primaryBtn(false)} onClick={() => chooseRoute('skin')}>Book a consultation instead</button>
                  </>
                )}
                <button onClick={() => setStep('reason')} style={{ background: 'none', border: 'none', color: C.mut, fontSize: '.82rem', cursor: 'pointer', marginTop: 12, fontFamily: 'inherit' }}>← Back</button>
              </>
            )}

            {step === 'mode' && (
              <>
                {bubble(<>How would you like to consult?</>)}
                <button style={rowBtn} disabled={busy} onClick={() => chooseMode('clinic')}><span style={{ fontSize: 20 }}>🏥</span><span>In-person at the clinic</span></button>
                <button style={rowBtn} disabled={busy} onClick={() => chooseMode('online')}><span style={{ fontSize: 20 }}>🎥</span><span>Video consultation</span></button>
                <button onClick={() => setStep(route === 'procedure' ? 'procedure' : 'concern')} style={{ background: 'none', border: 'none', color: C.mut, fontSize: '.82rem', cursor: 'pointer', marginTop: 6, fontFamily: 'inherit' }}>← Back</button>
              </>
            )}

            {step === 'book' && (
              <>
                {bubble(doctor ? <>Booking with <b>{doctor.name}</b>{doctor.fee ? ` · ₹${doctor.fee}` : ''} · {mode === 'online' ? 'Video' : 'Clinic'}. Pick a date and time.</> : <>Pick a date and time.</>)}
                {avail.length === 0 ? (
                  <p style={{ fontSize: '.9rem', color: C.mut }}>No open slots in the next 2 weeks. Please try "Request a call".</p>
                ) : (
                  <>
                    <div style={{ fontSize: '.72rem', fontWeight: 700, color: C.mut, textTransform: 'uppercase', letterSpacing: '.04em', margin: '6px 0' }}>Date</div>
                    <div>{avail.map(a => <span key={a.date} style={chip(a.date === selDate)} onClick={() => { setSelDate(a.date); setSelSlot('') }}>{fmtDate(a.date)}</span>)}</div>
                    <div style={{ fontSize: '.72rem', fontWeight: 700, color: C.mut, textTransform: 'uppercase', letterSpacing: '.04em', margin: '12px 0 6px' }}>Time</div>
                    <div>{(dayObj?.free || []).map(s => <span key={s} style={chip(s === selSlot)} onClick={() => setSelSlot(s)}>{s}</span>)}</div>
                    <button style={primaryBtn(!selDate || !selSlot)} disabled={!selDate || !selSlot} onClick={goToDetails}>
                      {selSlot ? `Continue — ${fmtDate(selDate)}, ${selSlot}` : 'Pick a time'}
                    </button>
                  </>
                )}
                <button onClick={() => setStep('mode')} style={{ background: 'none', border: 'none', color: C.mut, fontSize: '.82rem', cursor: 'pointer', marginTop: 12, fontFamily: 'inherit' }}>← Back</button>
              </>
            )}

            {step === 'details' && (
              <>
                {bubble(<>Almost done — a few details for the doctor.</>)}
                <input style={inpStyle} placeholder="Full name" value={fullName} onChange={e => setFullName(e.target.value)} />
                <div style={{ fontSize: '.74rem', color: C.mut, margin: '10px 0 4px' }}>Date of birth (optional)</div>
                <input style={inpStyle} type="date" value={dob} onChange={e => setDob(e.target.value)} />
                <div style={{ fontSize: '.74rem', color: C.mut, margin: '10px 0 4px' }}>Gender (optional)</div>
                <div style={{ marginBottom: 12 }}>{['male', 'female', 'other'].map(g => <span key={g} style={chip(g === gender)} onClick={() => setGender(g === gender ? '' : g)}>{g[0].toUpperCase() + g.slice(1)}</span>)}</div>
                <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: '.8rem', color: C.mut, cursor: 'pointer' }}>
                  <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} style={{ marginTop: 3 }} />
                  <span>{CONSENT_TEXT}</span>
                </label>
                <button style={primaryBtn(!fullName.trim() || !consent)} disabled={!fullName.trim() || !consent} onClick={goToReview}>Review booking</button>
                <button onClick={() => setStep('book')} style={{ background: 'none', border: 'none', color: C.mut, fontSize: '.82rem', cursor: 'pointer', marginTop: 10, fontFamily: 'inherit' }}>← Back</button>
              </>
            )}

            {step === 'review' && (
              <>
                {bubble(<>Please review your booking before confirming.</>)}
                <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: '14px 16px', fontSize: '.88rem', lineHeight: 1.75 }}>
                  <div><b>{fullName}</b></div>
                  <div>{routeCfg.label}{concern ? ` — ${concern}` : ''}</div>
                  <div>{mode === 'online' ? '🎥 Video consultation' : '🏥 In-person at the clinic'}</div>
                  <div><b>{fmtDate(selDate)}</b> at <b>{selSlot}</b></div>
                  <div style={{ color: C.mut }}>{doctor?.name || 'Dr. Lakshmi Priya'}{doctor?.fee ? ` · ₹${doctor.fee}` : ''}</div>
                </div>
                <button style={primaryBtn(busy)} disabled={busy} onClick={confirmBooking}>{busy ? 'Confirming…' : 'Confirm booking'}</button>
                <button onClick={() => setStep('details')} style={{ background: 'none', border: 'none', color: C.mut, fontSize: '.82rem', cursor: 'pointer', marginTop: 10, fontFamily: 'inherit' }}>← Back</button>
              </>
            )}

            {step === 'done' && (
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <div style={{ fontSize: 40 }}>✅</div>
                <p style={{ fontSize: '1.02rem', fontWeight: 800, margin: '8px 0 4px' }}>{booked?.status === 'confirmed' ? 'Appointment confirmed' : 'Appointment requested'}</p>
                <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: '12px 14px', fontSize: '.88rem', textAlign: 'left', margin: '10px 0' }}>
                  {doctor?.name || 'Dr. Lakshmi Priya'}<br />
                  <b>{fmtDate(booked?.date || selDate)}</b> at <b>{booked?.timeSlot || selSlot}</b> · {mode === 'online' ? 'Video' : 'Clinic'}<br />
                  <span style={{ color: booked?.status === 'confirmed' ? C.indigoD : '#B7770D', fontWeight: 700 }}>{booked?.status === 'confirmed' ? '✓ Confirmed' : 'Pending — pay to confirm'}</span>
                </div>
                {uploadDone ? (
                  <p style={{ fontSize: '.86rem', color: C.indigoD, margin: '6px 0' }}>📷 Photos received — the doctor will see them before your visit.</p>
                ) : (
                  <>
                    <p style={{ fontSize: '.84rem', color: C.mut, margin: '6px 0' }}>Add photos of your concern so the doctor can review them before your visit (optional).</p>
                    <label style={{ ...ghostBtn, display: 'inline-block', boxSizing: 'border-box' }}>
                      {uploading ? 'Uploading…' : '📷 Add photos'}
                      <input type="file" accept="image/*" multiple hidden disabled={uploading} onChange={e => uploadPhotos(e.target.files)} />
                    </label>
                  </>
                )}
                <button onClick={reset} style={primaryBtn(false)}>Done</button>
              </div>
            )}

            {step === 'called' && (
              <div style={{ textAlign: 'center', padding: '14px 0' }}>
                <div style={{ fontSize: 40 }}>📞</div>
                <p style={{ fontSize: '1rem', fontWeight: 700, margin: '10px 0 4px' }}>We're calling you now</p>
                <p style={{ fontSize: '.88rem', color: C.mut, margin: 0 }}>Please pick up the call on <b>{clean}</b>.</p>
                <button onClick={reset} style={primaryBtn(false)}>Done</button>
              </div>
            )}

            {step === 'chat' && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {chatMsgs.map((m, i) => (
                    <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', background: m.role === 'user' ? C.indigo : C.card, color: m.role === 'user' ? '#fff' : C.ink, border: m.role === 'user' ? 'none' : `1px solid ${C.line}`, padding: '10px 13px', borderRadius: m.role === 'user' ? '16px 16px 6px 16px' : '6px 16px 16px 16px', fontSize: '.9rem', maxWidth: '88%', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{m.text}</div>
                  ))}
                  {chatBusy && <div style={{ alignSelf: 'flex-start', color: C.mut, fontSize: '.85rem', padding: '2px 4px' }}>typing…</div>}
                </div>
                <button onClick={() => setStep('phone')} style={{ ...ghostBtn, marginTop: 14 }}>📅 Book an appointment</button>
              </>
            )}

            {note && <p style={{ fontSize: '.78rem', color: C.indigoD, marginTop: 12 }}>{note}</p>}
            {err && <p style={{ fontSize: '.82rem', color: C.err, marginTop: 12 }}>{err}</p>}
          </div>

          {/* Bottom input bar (typing steps only) */}
          {typingStep && (
            <div style={{ padding: '12px 14px 8px', background: C.card, borderTop: `1px solid ${C.line}`, flexShrink: 0 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  style={{ flex: 1, padding: '.8rem 1rem', border: `1px solid ${C.line}`, borderRadius: 999, fontSize: '.95rem', fontFamily: 'inherit', outline: 'none', background: C.bg }}
                  inputMode={barMode} placeholder={barPlaceholder} value={barValue}
                  onChange={e => barSet(e.target.value)} onKeyDown={e => e.key === 'Enter' && barSubmit()} autoFocus
                />
                <button onClick={barSubmit} disabled={busy || chatBusy} aria-label="Send"
                  style={{ width: 44, height: 44, flexShrink: 0, borderRadius: '50%', border: 'none', background: (busy || chatBusy) ? '#a9a7e0' : C.indigo, color: '#fff', fontSize: 20, cursor: (busy || chatBusy) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {(busy || chatBusy) ? '…' : '→'}
                </button>
              </div>
            </div>
          )}

          {/* Footer disclaimer */}
          <div style={{ padding: '8px 14px 12px', background: C.card, textAlign: 'center', fontSize: '.72rem', color: C.mut, flexShrink: 0, borderTop: typingStep ? 'none' : `1px solid ${C.line}` }}>
            🔒 Secure · The doctor makes every clinical decision
          </div>
        </div>
      )}
    </>
  )
}

const inpStyle = { width: '100%', padding: '.75rem .9rem', border: '1px solid #e6e8f0', borderRadius: 12, fontSize: '.95rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', background: '#f4f5f9' }
